/**
 * wijzigingSync — meldt rooster-wijzigingen aan het server-wijzigingslogboek
 * (POST /api/wijzigingen).
 *
 * Zelfde rolverdeling als logSync.js: de store legt alleen DATA vast, deze module buffert
 * en verstuurt. Zo blijft de store vrij van netwerkcode en hoeft geen enkele store-actie
 * async te worden.
 *
 * BEWUST ANDERS DAN logSync.js — geen localStorage-spiegel en geen opnieuw-proberen. Een
 * gemiste melding is acceptabel, een VERKEERDE niet: de server stempelt mwKey/naam/tijdstip
 * uit het token op het MOMENT van versturen, dus een record dat een reload of een nieuwe
 * sessie overleeft zou op naam van de verkeerde persoon (of met een verkeerd tijdstip)
 * kunnen landen. Liever een gat in het logboek dan een onjuiste regel erin.
 *
 * Anti-lus (letterlijk zoals logSync.js en devLogSink.js): deze module roept NOOIT logError
 * of log aan en laat geen enkele fout ontsnappen — fire-and-forget.
 */
import { stuurWijzigingen, WIJZIGINGEN_OPGESLAGEN_EVENT } from './api.js';
import { haalVersToken } from '../auth/huidigeGebruiker.js';

const DEBOUNCE_MS = 1500;
const BATCH_MAX = 50; // de server kapt zelf ook op 50 af (WIJZ_MAX_ITEMS)

let wachtrij = [];   // in-memory; bewust NIET gespiegeld naar localStorage
let timer = null;
let bezig = false;

/**
 * Meld één wijziging: in de wachtrij zetten en een gedempte flush plannen.
 * Het record draagt alleen rooster-gegevens (soort/week/cel); identiteit en tijdstip
 * komen server-side uit het token. Gooit nooit.
 */
export function meldWijziging(record) {
  if (!record || typeof record !== 'object') return;
  try {
    wachtrij.push(record);
    plan();
  } catch {
    /* stil — nooit via de logger */
  }
}

/** Plan een gedempte flush. Snel achter elkaar wijzigen → één POST. */
function plan() {
  if (timer !== null) return;
  try {
    timer = setTimeout(() => { timer = null; flush(); }, DEBOUNCE_MS);
  } catch {
    timer = null;
  }
}

/**
 * Verstuur de wachtrij in batches van maximaal BATCH_MAX.
 *
 * De batch wordt UIT DE WACHTRIJ GEHAALD VOOR de fetch: mislukt het versturen, dan zijn die
 * records stil verloren en gaan ze er NIET in terug. Terugzetten zou ze bij een latere flush
 * alsnog laten landen — met een tijdstip dat niet meer klopt en mogelijk onder een andere
 * ingelogde gebruiker.
 *
 * Zonder token wordt er niet verstuurd en wordt de wachtrij geleegd: de server weigert een
 * tokenloze POST met 401, en postJson zou daarop de sessie-verlopen-overlay tonen. Dat is
 * geen "stil" verlies meer, dus vangen we het hier al af.
 */
async function flush() {
  if (bezig) return;
  bezig = true;
  try {
    if (wachtrij.length === 0) return;

    let token = null;
    try { token = await haalVersToken(); } catch { token = null; }
    if (!token) { wachtrij = []; return; }

    while (wachtrij.length > 0) {
      const batch = wachtrij.slice(0, BATCH_MAX);
      wachtrij = wachtrij.slice(batch.length); // eerst uit de wachtrij, dan pas versturen
      try {
        await stuurWijzigingen(batch);
        // Pas NA een geslaagde POST, en alleen als er cel-records in zaten: het handmatig-teken
        // in de matrix leest store.wijzigingen, en dat veld komt normaal alleen bij het
        // opstarten binnen (serverSync.laadVanServer). Zonder dit signaal verschijnt de stip
        // pas na een F5. Een mislukte POST verandert niets aan de serverstand, dus die hoort
        // hier geen herophaal te veroorzaken — dan zou het alleen ruis zijn.
        // Een EVENT en geen store-aanroep: dit bestand houdt zich bewust buiten de store (zie
        // de kop). serverSync luistert en doet de smalle update. Zelfde patroon als
        // LOGS_GEZIEN_EVENT (api.js).
        if (batch.some((r) => r?.soort === 'cel') && typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent(WIJZIGINGEN_OPGESLAGEN_EVENT));
        }
      } catch {
        break; // stil verlies: niet terugzetten, niet loggen, niet opnieuw proberen
      }
    }
  } catch {
    // Anti-lus: een fout hier mag nooit doorgegooid of gelogd worden.
  } finally {
    bezig = false;
  }
}
